import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { clearUserOnboardingData, getInitialOnboardingState } from '../services/onboardingTestService';
import { toast } from 'react-hot-toast';
import OnboardingFlow from './OnboardingFlow';

/**
 * OnboardingTestFlow Component
 * 
 * Provides a testing pathway for the onboarding flow that:
 * 1. Clears existing onboarding data for the logged-in user
 * 2. Presents the standard onboarding flow with fresh state
 * 3. Handles errors and provides user feedback during the reset process
 */
const OnboardingTestFlow = () => {
    const { user } = useAuthContext();

    // State for managing the reset process
    const [isResetting, setIsResetting] = useState(true);
    const [resetError, setResetError] = useState(null);
    const [resetComplete, setResetComplete] = useState(false);

    // Trigger data reset when component mounts
    useEffect(() => {
        const performDataReset = async () => {
            if (!user?.uid) {
                setResetError('User not authenticated');
                setIsResetting(false);
                return;
            }

            try {
                console.log('[OnboardingTestFlow] Starting onboarding data reset...');

                // Clear the user's onboarding data
                const result = await clearUserOnboardingData(user.uid);

                if (result.success) {
                    console.log('[OnboardingTestFlow] Data reset successful');

                    // Show success message
                    toast.success('Onboarding data reset successfully!');

                    // Log any non-blocking cleanup warnings
                    if (result.profilePictureCleanupError) {
                        console.warn('[OnboardingTestFlow] Profile picture cleanup warning:', result.profilePictureCleanupError);
                    }

                    // Mark reset as complete
                    setResetComplete(true);
                } else {
                    console.error('[OnboardingTestFlow] Data reset failed:', result.error);
                    setResetError(result.error || 'Failed to reset onboarding data');
                    toast.error('Failed to reset onboarding data');
                }
            } catch (error) {
                console.error('[OnboardingTestFlow] Unexpected error during reset:', error);
                setResetError('An unexpected error occurred during reset');
                toast.error('An unexpected error occurred');
            } finally {
                setIsResetting(false);
            }
        };

        performDataReset();
    }, [user?.uid]);

    // Handle retry functionality
    const handleRetry = () => {
        setIsResetting(true);
        setResetError(null);
        setResetComplete(false);

        // Re-trigger the reset process
        setTimeout(() => {
            window.location.reload();
        }, 100);
    };

    // Render loading state during reset
    if (isResetting) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-500 mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        Preparing Test Environment
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        Resetting your onboarding data for testing...
                    </p>
                </div>
            </div>
        );
    }

    // Render error state if reset failed
    if (resetError && !resetComplete) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="max-w-md w-full mx-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
                        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>

                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            Reset Failed
                        </h2>

                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {resetError}
                        </p>

                        <div className="space-y-3">
                            <button
                                onClick={handleRetry}
                                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                            >
                                Try Again
                            </button>


                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Render success confirmation before showing onboarding flow
    if (resetComplete) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                {/* Test Mode Banner */}
                <div className="bg-yellow-100 dark:bg-yellow-900 border-b border-yellow-200 dark:border-yellow-800">
                    <div className="max-w-7xl mx-auto px-4 py-2">
                        <div className="flex items-center justify-center">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                    TEST MODE: Your onboarding data has been reset for testing
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Render the standard onboarding flow */}
                <OnboardingFlow />
            </div>
        );
    }

    // Fallback render (shouldn't reach here normally)
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
            <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400">
                    Initializing test environment...
                </p>
            </div>
        </div>
    );
};

export default OnboardingTestFlow;